import axios from 'axios';

import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";


import ProfilePicture from "./ProfilePicture";
import { ACCENT_COLOR } from "../constants/colors";
import Loading from './Loading';
import PageHeaderCapsuleButton from './PageHeaderCapsuleButton';
import PrimaryActionButton from './PrimaryActionButton';
import {
    ChevronLeftRounded,
    ChevronRightRounded,
    RemoveRounded,
} from '@mui/icons-material';
import { SwipeableDrawer } from '@mui/material';
import {
    GET_STARTED_DATA,
} from '../constants/local_storage_constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { trackOnboardingError, trackOnboardingEvent } from '../utils/onboardingFunnel';
import { getStartedTopicDisplayName } from '../utils/getStartedTopicDisplayName';
import { addAppScrollListener, getAppViewport } from '../utils/appViewport';
import {
    clearGetStartedDraft,
    getStartedPairKey,
    getStartedSelectionPayload,
    persistGetStartedDraft,
    readGetStartedDraft,
    reconcileGetStartedDraft,
} from '../utils/getStartedSelections';

const canonicalSpeakers = (speakers = []) => {
    const seenSpeakerIds = new Set();

    return speakers.reduce((result, speaker) => {
        const speakerIdKey = typeof speaker?.id === 'string'
            ? speaker.id.trim()
            : '';
        if (!speakerIdKey || seenSpeakerIds.has(speakerIdKey)) {
            return result;
        }

        seenSpeakerIds.add(speakerIdKey);
        result.push({ ...speaker, id: speakerIdKey });
        return result;
    }, []);
};

const canonicalGetStartedData = (getStartedData) => ({
    ...getStartedData,
    data: (getStartedData?.data || []).reduce((topics, topicInfo) => {
        const speakers = canonicalSpeakers(topicInfo?.speakers);
        if (speakers.length > 0) {
            topics.push({
                ...topicInfo,
                speakers,
            });
        }
        return topics;
    }, []),
});

const GetStartedTopicPreview = ({
    info,
    selectedPairKeys,
    speakerClick,
    topicIndex,
    headingTag: Heading = 'h2',
}) => {
    const speakerRowRef = useRef(null);
    const [scrollState, setScrollState] = useState(() => ({
        canScrollNext: info.speakers.length > 4,
        canScrollPrevious: false,
        hasOverflow: info.speakers.length > 4,
    }));
    const topicHeadingId = `get-started-topic-${topicIndex}`;
    const speakerRowId = `get-started-speaker-row-${topicIndex}`;
    const topicDisplayName = getStartedTopicDisplayName(info);

    const updateScrollState = useCallback(() => {
        const speakerRow = speakerRowRef.current;
        if (!speakerRow) {
            return;
        }
        if (speakerRow.clientWidth <= 0) {
            return;
        }

        const maximumScrollLeft = Math.max(
            0,
            speakerRow.scrollWidth - speakerRow.clientWidth,
        );
        const nextScrollState = {
            canScrollNext: speakerRow.scrollLeft < maximumScrollLeft - 2,
            canScrollPrevious: speakerRow.scrollLeft > 2,
            hasOverflow: maximumScrollLeft > 2,
        };
        setScrollState((currentScrollState) => {
            if (
                currentScrollState.canScrollNext
                    === nextScrollState.canScrollNext
                && currentScrollState.canScrollPrevious
                    === nextScrollState.canScrollPrevious
                && currentScrollState.hasOverflow
                    === nextScrollState.hasOverflow
            ) {
                return currentScrollState;
            }
            return nextScrollState;
        });
    }, []);

    useLayoutEffect(() => {
        const speakerRow = speakerRowRef.current;
        if (!speakerRow) {
            return undefined;
        }

        updateScrollState();
        const animationFrame = window.requestAnimationFrame?.(
            updateScrollState,
        );
        speakerRow.addEventListener('scroll', updateScrollState, {
            passive: true,
        });
        window.addEventListener('resize', updateScrollState);
        window.addEventListener('load', updateScrollState);

        const resizeObserver = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(updateScrollState);
        resizeObserver?.observe(speakerRow);
        if (speakerRow.parentElement) {
            resizeObserver?.observe(speakerRow.parentElement);
        }

        return () => {
            if (animationFrame !== undefined) {
                window.cancelAnimationFrame?.(animationFrame);
            }
            speakerRow.removeEventListener('scroll', updateScrollState);
            window.removeEventListener('resize', updateScrollState);
            window.removeEventListener('load', updateScrollState);
            resizeObserver?.disconnect();
        };
    }, [info.speakers.length, updateScrollState]);

    const moveSpeakerRow = useCallback((requestedDistance, behavior) => {
        const speakerRow = speakerRowRef.current;
        if (!speakerRow) {
            return false;
        }

        const maximumScrollLeft = Math.max(
            0,
            speakerRow.scrollWidth - speakerRow.clientWidth,
        );
        const nextScrollLeft = Math.min(
            maximumScrollLeft,
            Math.max(0, speakerRow.scrollLeft + requestedDistance),
        );
        const appliedDistance = nextScrollLeft - speakerRow.scrollLeft;
        if (Math.abs(appliedDistance) < 1) {
            return false;
        }

        if (typeof speakerRow.scrollBy === 'function') {
            speakerRow.scrollBy({
                behavior,
                left: appliedDistance,
            });
        } else {
            speakerRow.scrollLeft = nextScrollLeft;
            updateScrollState();
        }
        window.requestAnimationFrame?.(updateScrollState);
        return true;
    }, [updateScrollState]);

    const scrollSpeakers = (direction) => () => {
        const speakerRow = speakerRowRef.current;
        if (!speakerRow) {
            return;
        }
        const reduceMotion = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        const maximumScrollLeft = Math.max(
            0,
            speakerRow.scrollWidth - speakerRow.clientWidth,
        );
        const remainingDistance = direction > 0
            ? maximumScrollLeft - speakerRow.scrollLeft
            : speakerRow.scrollLeft;
        // Finish the last page explicitly; proximity snapping varies by browser.
        const distance = remainingDistance <= speakerRow.clientWidth
            ? remainingDistance
            : Math.max(speakerRow.clientWidth * 0.78, 220);
        moveSpeakerRow(
            direction * distance,
            reduceMotion ? 'auto' : 'smooth',
        );
    };

    useEffect(() => {
        const speakerRow = speakerRowRef.current;
        if (!speakerRow) {
            return undefined;
        }

        const handleWheel = (event) => {
            const horizontalDelta = Math.abs(event.deltaX)
                > Math.abs(event.deltaY)
                ? event.deltaX
                : event.shiftKey
                    ? event.deltaY
                    : 0;
            if (horizontalDelta === 0) {
                return;
            }

            const deltaMultiplier = event.deltaMode === 1
                ? 24
                : event.deltaMode === 2
                    ? speakerRow.clientWidth
                    : 1;
            const didMove = moveSpeakerRow(
                horizontalDelta * deltaMultiplier,
                'auto',
            );
            if (didMove) {
                event.preventDefault();
            }
        };

        speakerRow.addEventListener('wheel', handleWheel, {
            passive: false,
        });
        return () => {
            speakerRow.removeEventListener('wheel', handleWheel);
        };
    }, [moveSpeakerRow]);

    return (
        <section
            aria-labelledby={topicHeadingId}
            className='speakerPack'
        >
            <div className='getStartedTopicHeader'>
                <Heading
                    className='getStartedTopicHeading'
                    id={topicHeadingId}
                >
                    <span
                        aria-hidden='true'
                        className='getStartedTopicEmoji'
                    >
                        {info["emoji"]}
                    </span>
                    <span>{topicDisplayName}</span>
                </Heading>

                {scrollState.hasOverflow && (
                    <div
                        aria-label={`Browse ${topicDisplayName} speakers`}
                        className='getStartedSpeakerScrollControls'
                        role='group'
                    >
                        <button
                            aria-controls={speakerRowId}
                            aria-label={`Previous ${topicDisplayName} speakers`}
                            className='getStartedSpeakerScrollButton'
                            disabled={!scrollState.canScrollPrevious}
                            onClick={scrollSpeakers(-1)}
                            type='button'
                        >
                            <ChevronLeftRounded aria-hidden='true' />
                        </button>
                        <button
                            aria-controls={speakerRowId}
                            aria-label={`Next ${topicDisplayName} speakers`}
                            className='getStartedSpeakerScrollButton'
                            disabled={!scrollState.canScrollNext}
                            onClick={scrollSpeakers(1)}
                            type='button'
                        >
                            <ChevronRightRounded aria-hidden='true' />
                        </button>
                    </div>
                )}
            </div>

            <div
                className='getStartedSpeakerRowFrame'
                data-can-scroll-previous={scrollState.canScrollPrevious}
                data-can-scroll-next={scrollState.canScrollNext}
            >
                <div
                    aria-label={`${topicDisplayName} speakers`}
                    className='getStartedSpeakerRow'
                    id={speakerRowId}
                    ref={speakerRowRef}
                    role='group'
                >
                    {info["speakers"].map((speaker) => {
                        const isSelected = selectedPairKeys.has(getStartedPairKey({
                            speakerId: speaker.id, topic: info.topic,
                        }));
                        const speakerName = speaker.name || 'Speaker';

                        return (
                            <div
                                className='getStartedSpeakerPreviewItem'
                                key={speaker.id}
                            >
                                <button
                                    aria-label={`${isSelected ? 'Deselect' : 'Select'} ${speakerName}`}
                                    aria-pressed={isSelected}
                                    className={[
                                        'getStartedSpeakerPreviewButton',
                                        isSelected
                                            ? 'getStartedSpeakerPreviewButtonSelected'
                                            : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={speakerClick(speaker.id, info.topic)}
                                    type='button'
                                >
                                    <span className='getStartedSpeakerPreviewAvatar'>
                                        <ProfilePicture
                                            borderSize={2}
                                            color={isSelected
                                                ? ACCENT_COLOR
                                                : 'var(--app-surface-tertiary)'}
                                            imageSrc={speaker.imageURL}
                                            margin='0px'
                                            size='88px'
                                        />
                                        {isSelected && (
                                            <span
                                                aria-hidden='true'
                                                className='getStartedSpeakerSelectionCheck'
                                            >
                                                ✓
                                            </span>
                                        )}
                                    </span>
                                    <span className='getStartedSpeakerPreviewName'>
                                        {speakerName}
                                    </span>
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};


const GetStartedPickSpeakers = ({
    embeddedInHome = false,
    guest = true,
    onSelectionChange,
}) => {

    const navigate = useNavigate();
    const location = useLocation();
    const analytics = getAnalytics();
    const Heading = embeddedInHome ? 'h2' : 'h1';
    const pickerRef = useRef(null);
    const continueSurfaceRef = useRef(null);

    const [selection, setSelection] = useState(readGetStartedDraft);
    const selectedPairs = selection.selectedPairs;
    const selectedSpeakerIds = useMemo(() => new Set(
        selectedPairs.map(pair => pair.speakerId),
    ), [selectedPairs]);
    const selectedPairKeys = useMemo(() => new Set(
        selectedPairs.map(getStartedPairKey),
    ), [selectedPairs]);
    const [showSelectedSpeakers, setShowSelectedSpeakers] = useState(false);

    const [getStartedData, setGetStartedData] = useState(null);
    const [getStartedDataError, setGetStartedDataError] = useState(false);
    const [rosterAttempt, setRosterAttempt] = useState(0);
    const [highlightsUnavailable, setHighlightsUnavailable] = useState(location.state?.highlightsUnavailable === true);

    useEffect(() => {
        const picker = pickerRef.current;
        if (!guest || !getStartedData || !picker) return undefined;
        let active = true;
        const viewed = () => { if (active) trackOnboardingEvent('onboarding_picker_view', {}, { start: true }); };
        if (typeof IntersectionObserver === 'function') {
            const observer = new IntersectionObserver(entries => {
                if (entries.some(entry => entry.isIntersecting)) viewed();
            });
            observer.observe(picker);
            return () => { active = false; observer.disconnect(); };
        }
        const check = () => {
            const rect = picker.getBoundingClientRect();
            const viewport = getAppViewport();
            if (rect.bottom > viewport.top && rect.top < viewport.bottom) viewed();
        };
        check();
        const removeScrollListener = addAppScrollListener(check);
        return () => { active = false; removeScrollListener(); };
    }, [getStartedData, guest]);

    useEffect(() => {
        setHighlightsUnavailable(location.state?.highlightsUnavailable === true);
    }, [location.state?.highlightsUnavailable]);

    useLayoutEffect(() => {
        const picker = pickerRef.current;
        const surface = continueSurfaceRef.current;
        if (!guest || !picker || !surface) return undefined;
        const measure = () => {
            const height = surface.getBoundingClientRect().height;
            if (height > 0) picker.style.setProperty('--get-started-actions-height', `${height}px`);
        };
        measure();
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
        observer?.observe(surface);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
            picker.style.removeProperty('--get-started-actions-height');
        };
    }, [getStartedData, guest, selectedSpeakerIds.size]);

    useEffect(() => {
        setGetStartedDataError(false);
        let cancelled = false;
        axios
            .get('/get_started')
            .then((res) => {
                if (cancelled) return;
                const nextGetStartedData = canonicalGetStartedData(res.data);
                if (!nextGetStartedData.data.length) {
                    setGetStartedDataError(true);
                    if (guest) trackOnboardingError('picker', { code: 'empty_roster' });
                    return;
                }
                setGetStartedData(nextGetStartedData);
                setSelection(savedDraft => reconcileGetStartedDraft(savedDraft, nextGetStartedData));
            })
            .catch((err) => {
                if (cancelled) return;
                if (guest) trackOnboardingError('picker', err);
                setGetStartedDataError(true);
            });
        return () => { cancelled = true; };
    }, [guest, rosterAttempt]);

    useEffect(() => {
        if (getStartedData) persistGetStartedDraft(selection);
        if (guest && getStartedData && selectedSpeakerIds.size > 0) trackOnboardingEvent('onboarding_pick');
        onSelectionChange?.(selectedSpeakerIds.size);
    }, [getStartedData, guest, onSelectionChange, selection, selectedSpeakerIds]);

    useEffect(() => {
        if (selectedSpeakerIds.size === 0) {
            setShowSelectedSpeakers(false);
        }
    }, [selectedSpeakerIds]);

    const selectedSpeakers = getStartedData
        ? canonicalSpeakers(
            getStartedData.data.flatMap((topic) => topic.speakers),
        ).filter((speaker) => selectedSpeakerIds.has(speaker.id))
        : [];

    const speakerClick = (speakerId, topic) => () => {
        if (guest && !selectedPairKeys.has(getStartedPairKey({ speakerId, topic }))) {
            trackOnboardingEvent('onboarding_pick');
        }
        setHighlightsUnavailable(false);
        setSelection(current => {
            const pair = { speakerId, topic };
            const key = getStartedPairKey(pair);
            const isSelected = current.selectedPairs.some(selected => getStartedPairKey(selected) === key);
            return {
                ...current,
                picksChanged: false,
                selectedPairs: isSelected
                    ? current.selectedPairs.filter(selected => getStartedPairKey(selected) !== key)
                    : [...current.selectedPairs, pair],
            };
        });
    };

    const removeSpeaker = speakerId => () => {
        setHighlightsUnavailable(false);
        setSelection(current => ({
            ...current,
            picksChanged: false,
            selectedPairs: current.selectedPairs.filter(pair => pair.speakerId !== speakerId),
        }));
    };

    const savePicks = () => {
        logEvent(analytics, 'get_started_finish_picks', {
            ...(getStartedData.rosterVersion ? { roster_version: getStartedData.rosterVersion } : {}),
            selected_count: selectedSpeakerIds.size,
            selected_pair_count: selectedPairs.length,
        });
        localStorage.setItem(GET_STARTED_DATA, JSON.stringify({
            ...getStartedSelectionPayload(getStartedData, selectedPairs),
            selectedSpeakers: selectedSpeakers.map(({ id, name, imageURL }) => ({ id, name, imageURL })),
        }));
        clearGetStartedDraft();
        navigate('/curate_podcasts_for_you');
    };

    const continueActions = getStartedData && selectedSpeakerIds.size > 0
        ? (
            <div ref={continueSurfaceRef} className='getStartedContinueSurface'>
                <button
                    aria-expanded={showSelectedSpeakers}
                    aria-haspopup='dialog'
                    aria-label={`Review your picks, ${selectedSpeakerIds.size} selected`}
                    aria-live='polite'
                    className='getStartedSelectedCount'
                    onClick={() => setShowSelectedSpeakers(true)}
                    type='button'
                >
                    {selectedSpeakerIds.size} selected
                </button>
                <PrimaryActionButton
                    type='button'
                    onClick={savePicks}
                    className='getStartedContinueButton'
                >
                    Get my highlights
                </PrimaryActionButton>
            </div>
        )
        : null;

    return (
        <div ref={pickerRef} className={[
            'getStartedPicker',
            embeddedInHome ? 'getStartedPickerEmbedded' : '',
            embeddedInHome ? 'getStartedPickerInHome' : '',
            selectedSpeakerIds.size > 0
                ? 'getStartedPickerHasSelection'
                : '',
        ].filter(Boolean).join(' ')}>
            <div className='getStartedPickerHeader'>
                <Heading className={`${embeddedInHome ? 'homeDiscoveryHeading' : 'chatTitle homeHeroTitle'} getStartedPickerTitle`}>
                    Pick your people
                </Heading>
                {guest && <p className='homeHeroSubtitle getStartedPickerSubtitle'>
                    Hear highlights from your favorites
                </p>}
                {!guest && !embeddedInHome && <p className='homeHeroSubtitle getStartedPickerSubtitle'>
                    Choose a few voices{'\n'}you want to hear
                </p>}
                {highlightsUnavailable ? <p role='status'>
                    We couldn’t play those highlights. Try choosing another speaker.
                </p> : selection.picksChanged && <p role='status'>
                    Some of your saved picks are no longer available.
                </p>}
            </div>

            {!getStartedData ?
                <div>
                    {getStartedDataError ? <div role='alert'>
                        Couldn’t load speaker choices. Your picks are safe.
                        <button type='button' onClick={() => setRosterAttempt(attempt => attempt + 1)}>Try again</button>
                    </div> : <div><Loading overrideStyle={{ padding: '20px', textAlign: 'center' }} /></div>}
                </div>
                :
                <div className='speakerPacksContainer animate__animated animate__fadeIn'>
                    {getStartedData.data.map((info, topicIndex) => (
                        <div key={`${info.topic}-${topicIndex}`}>
                            <GetStartedTopicPreview
                                headingTag={embeddedInHome ? 'h3' : 'h2'}
                                info={info}
                                selectedPairKeys={selectedPairKeys}
                                speakerClick={speakerClick}
                                topicIndex={topicIndex}
                            />
                        </div>
                    ))}
                </div>
            }

            {continueActions && (
                <div className={[
                    'getStartedContinueDock',
                    embeddedInHome ? 'getStartedContinueDockAboveBottomMenu' : '',
                ].filter(Boolean).join(' ')}>
                    <div className='horizontalCenter getStartedContinueDockContent'>
                        {continueActions}
                    </div>
                </div>
            )}

            <SwipeableDrawer
                anchor='bottom'
                open={showSelectedSpeakers}
                onClose={() => setShowSelectedSpeakers(false)}
                onOpen={() => setShowSelectedSpeakers(true)}
                disableDiscovery
                ModalProps={{ keepMounted: false }}
                PaperProps={{
                    sx: {
                        borderRadius: '18px 18px 0px 0px',
                        maxHeight: 'calc(var(--app-drawer-height, 100vh) * 0.72)',
                    },
                }}
            >
                <section
                    aria-label='Your picks'
                    className='getStartedSelectedSpeakersDrawer horizontalCenter appDrawerSurface'
                    role='dialog'
                >
                    <div className='previewPreferenceDragIndicator' />
                    <div className='getStartedSelectedSpeakersHeader'>
                        <h2 className='getStartedSelectedSpeakersTitle'>
                            Your picks
                        </h2>
                        <PageHeaderCapsuleButton
                            aria-label='Close your picks'
                            className='getStartedSelectedSpeakersCloseButton'
                            onClick={() => setShowSelectedSpeakers(false)}
                            type='button'
                        >
                            Close
                        </PageHeaderCapsuleButton>
                    </div>
                    <div className='highlightsDrawerDivider' />
                    <div className='getStartedSelectedSpeakersRows hideScrollbar'>
                        {selectedSpeakers.map((speaker) => {
                            const speakerName = speaker.name || 'Speaker';
                            return (
                                <div
                                    className='getStartedSelectedSpeakerRow'
                                    key={speaker.id}
                                >
                                    <ProfilePicture
                                        borderSize={1}
                                        color='var(--app-surface-tertiary)'
                                        imageSrc={speaker.imageURL}
                                        margin='0px'
                                        size='48px'
                                    />
                                    <span className='getStartedSelectedSpeakerName'>
                                        {speakerName}
                                        <br />
                                        <small>{getStartedData.data
                                            .filter(row => selectedPairKeys.has(getStartedPairKey({
                                                speakerId: speaker.id, topic: row.topic,
                                            })))
                                            .map(getStartedTopicDisplayName).join(' · ')}</small>
                                    </span>
                                    <button
                                        aria-label={`Remove ${speakerName} from your picks`}
                                        className='getStartedSelectedSpeakerRemoveButton'
                                        onClick={removeSpeaker(speaker.id)}
                                        type='button'
                                    >
                                        <RemoveRounded aria-hidden='true' />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </SwipeableDrawer>

        </div>
    );
};

export default GetStartedPickSpeakers;
